import { useEffect, useRef, useSyncExternalStore } from "react";
import type { OpsPresentationPrivacy, OpsPublicTurnEnvelope, OpsSha256 } from "@business-ops/domain";
import type { OpsAssistantHistoryMessage } from "../api.js";
import { api, ApiError } from "../api.js";
import {
  createBrowserOpsClientFreshnessGate,
  type OpsClientFreshnessGate
} from "./ops-client-freshness.js";
import type { OpsMediaOwnerController } from "./ops-media-owner.js";
import {
  getOpsRealtimeVoiceUnsupportedReason,
  openOpsRealtimeVoiceSession,
  safeOpsRealtimeVoiceFatalCode,
  type OpenOpsRealtimeVoiceOptions,
  type OpsRealtimeVoiceEvent,
  type OpsRealtimeVoiceSession
} from "./ops-realtime-voice.js";
import {
  createOpsVoiceTurnId,
  recordOpsVoiceMetric
} from "./ops-voice-metrics.js";

export type OpsVoiceMode = "text" | "voice";
type OpsVoiceSyncTimingName = "publish" | "assistantRender" | "requestHistory"
  | "visibleSessionTurns" | "visiblePublicTurn" | "persistConversation";
type OpsVoiceSyncTiming = {
  enabled: boolean;
  rows: Partial<Record<OpsVoiceSyncTimingName, { count: number; totalMs: number; maxMs: number }>>;
};

// WHY [BOL-VOICE-SYNC-OWNER-DIAGNOSTIC-001]: R348 capture overflow coincided
// with long main-thread tasks; R349 attributed several unrelated callbacks,
// not their common synchronous consumer. This opt-in six-row aggregate measures
// that consumer without retaining arguments, history, PCM, or event content.
// Durations are inclusive/nested, not additive CPU attribution. Disabled calls
// do not read the clock; collection caps at 10,000 samples per name. Falsifier:
// diagnostics change a result/exception/order or expose conversation content.
export function beginOpsVoiceSyncTiming(name: OpsVoiceSyncTimingName): (() => void) | undefined {
  try {
    if (typeof window === "undefined") return;
    const target = window as Window & { __opsVoiceSyncTiming?: OpsVoiceSyncTiming };
    const recording = target.__opsVoiceSyncTiming;
    if (recording?.enabled !== true || !recording.rows
      || (recording.rows[name]?.count ?? 0) >= 10_000) return;
    const startedAt = performance.now();
    return () => {
      try {
        if (target.__opsVoiceSyncTiming !== recording || recording.enabled !== true) return;
        const elapsed = Math.max(0, performance.now() - startedAt);
        const row = recording.rows[name] ?? { count: 0, totalMs: 0, maxMs: 0 };
        if (row.count >= 10_000) return;
        recording.rows[name] = {
          count: row.count + 1,
          totalMs: row.totalMs + elapsed,
          maxMs: Math.max(row.maxMs, elapsed)
        };
      } catch { /* Observation never replaces the measured result or exception. */ }
    };
  } catch { return; }
}

type OpsVoiceFatalCode = ReturnType<typeof safeOpsRealtimeVoiceFatalCode>
  | "client_freshness_rejected"
  | "activation_unsupported"
  | "activation_microphone_busy"
  | "toggle_mute_rejected"
  | "stop_microphone_busy"
  | "opened_mute_rejected"
  | "realtime_open_rejected"
  | "realtime_closed";
export type OpsVoiceMicReadiness = "idle" | "arming" | "ready" | "paused" | "error";
export type OpsVoicePhase =
  | "text_idle"
  | "voice_arming"
  | "listening"
  | "thinking"
  | "speaking"
  | "interrupting"
  | "error";

export interface OpsVoiceState {
  mode: OpsVoiceMode;
  micReadiness: OpsVoiceMicReadiness;
  phase: OpsVoicePhase;
  status: string;
  transcript: string;
  error: string | null;
  pulse: number;
  pulseIntensity: number;
  muted: boolean;
  supported: boolean;
  turnId: string | null;
  answerLatencyMs: number | null;
  firstAudioLatencyMs: number | null;
  firstInterimLatencyMs: number | null;
  finalizationLatencyMs: number | null;
  transportElapsedMs: number | null;
  speechRetryAvailable: boolean;
  approvalConsentPending: boolean;
}

export type OpsRealtimeTurnPresentation =
  | { type: "input-final"; turnId: string; transcript: string; source: "voice" | "typed";
      control?: "explicit_stop" }
  | { type: "input-revision"; turnId: string; transcript: string }
  | { type: "checking"; turnId: string }
  | { type: "acknowledgment"; turnId: string; text: string; presentationId?: string; revision?: number }
  | { type: "model-progress"; turnId: string; text: string }
  | { type: "status-cue"; turnId: string; text: string; cueId?: string; presentationId?: string }
  | { type: "public-turn"; turnId: string; publicTurn: OpsPublicTurnEnvelope }
  | { type: "assistant-partial"; turnId: string; transcript: string }
  | { type: "assistant-final"; turnId: string; transcript: string }
  | { type: "failed"; turnId: string; message: string }
  | { type: "interrupted"; turnId: string };

interface OpsRealtimeConversationBridge {
  history(): readonly OpsAssistantHistoryMessage[];
  historyReady?(): boolean;
  publicReleaseId?(): OpsSha256 | null;
  workspaceContext?(): unknown;
  presentationPrivacy?(): OpsPresentationPrivacy;
  presentationPrivacyLockChange?(locked: boolean): void;
  present(event: OpsRealtimeTurnPresentation): void | boolean | Promise<boolean>;
}

export interface OpsVoiceEnvironment {
  readTurnStatus?: typeof api.opsTurnStatus;
  cancelTurn?: typeof api.cancelOpsTextTurn;
  setTimer: (callback: () => void, milliseconds: number) => unknown;
  clearTimer: (timer: unknown) => void;
  now?: () => number;
  realtimeUnsupportedReason?: string | null;
  openRealtimeSession?: ((options: OpenOpsRealtimeVoiceOptions) => Promise<OpsRealtimeVoiceSession>) | null;
}

const INITIAL_STATE: OpsVoiceState = {
  mode: "text",
  micReadiness: "idle",
  phase: "text_idle",
  status: "Luna is ready.",
  transcript: "",
  error: null,
  pulse: 0,
  pulseIntensity: 0,
  muted: false,
  supported: false,
  turnId: null,
  answerLatencyMs: null,
  firstAudioLatencyMs: null,
  firstInterimLatencyMs: null,
  finalizationLatencyMs: null,
  transportElapsedMs: null,
  speechRetryAvailable: false,
  approvalConsentPending: false
};

const OPS_TRANSCRIPT_FINAL_RESUME_MS = 900;

export type OpsVoiceInterruptionIntent = "explicit_stop" | "backchannel" | "substantive" | "pending";

function normalizeOpsVoiceInterruption(transcript: string): string {
  return transcript.toLocaleLowerCase("en-US")
    .replace(/[’]/gu, "'")
    .replace(/[^\p{L}\p{N}'-]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

export function classifyOpsVoiceInterruption(
  transcript: string,
  final: boolean
): OpsVoiceInterruptionIntent {
  const normalized = normalizeOpsVoiceInterruption(transcript);
  if (!normalized) return "pending";
  if (/^(?:wait(?:\s+(?:a\s+)?(?:second|minute|moment))?|hold(?:\s+on)?|stop|no|nope|cancel)(?:\s|$)/u.test(normalized)) {
    return "explicit_stop";
  }
  if (final && /^(?:yeah|yes|yep|yup|uh-?huh|mm-?hmm|mhm|okay|ok|right|got it|sure|go on|continue)$/u.test(normalized)) {
    return "backchannel";
  }
  return final ? "substantive" : "pending";
}

function isOpsVoiceControlOnly(transcript: string): boolean {
  let remainder = normalizeOpsVoiceInterruption(transcript);
  if (!remainder) return false;
  const control = /^(?:wait(?:\s+(?:a\s+)?(?:second|minute|moment))?|hold(?:\s+on)?|stop|nope|no|cancel)(?:\s+|$)/u;
  let matched = false;
  while (control.test(remainder)) {
    matched = true;
    remainder = remainder.replace(control, "").trim();
  }
  return matched && remainder.length === 0;
}

function opsVoiceControlAction(transcript: string): "hold" | "cancel" | null {
  if (!isOpsVoiceControlOnly(transcript)) return null;
  const normalized = normalizeOpsVoiceInterruption(transcript);
  return /^(?:stop|no|nope|cancel)(?:\s|$)/u.test(normalized) ? "cancel" : "hold";
}

export class OpsVoiceController {
  private currentState: OpsVoiceState;
  private readonly listeners = new Set<() => void>();
  private generation = 0;
  private realtimeSession: OpsRealtimeVoiceSession | null = null;
  private realtimeOpenPromise: Promise<OpsRealtimeVoiceSession> | null = null;
  private realtimeExpiryRecoveryGeneration: number | null = null;
  private retainedCanonicalTurnId: string | null = null;
  private retainedCanonicalPoll: unknown = null;
  private currentTurnId: string | null = null;
  private activeBrainTurnId: string | null = null;
  private activeAudibleTurnId: string | null = null;
  private pausedOutputTurnId: string | null = null;
  private realtimeOutputResumeTimer: unknown = null;
  private readonly finalizedRealtimeTurns = new Set<string>();
  private readonly realtimeActivityStartedAt = new Map<string, number>();
  private readonly realtimeActivityEndedAt = new Map<string, number>();
  private readonly realtimeInterimSeen = new Set<string>();
  private readonly realtimeExplicitHoldInterimSeen = new Set<string>();
  private readonly realtimeFirstPartialAt = new Map<string, number>();
  private readonly realtimeContinuationTargetByTurn = new Map<string, string>();
  private readonly recoverableAudioFailedTurns = new Set<string>();
  private realtimePresentationUnavailable = false;
  private readonly presentationPrivacyLockedTurns = new Set<string>();
  private finalTranscriptAt = 0;
  private latestNativeAnswer: {
    text: string;
    generation: number;
    turnId: string;
  } | null = null;
  private activationRequest = 0;
  private mounted = false;
  private unregisterVoiceDisarm: (() => void) | null = null;

  constructor(
    private readonly mediaOwner: OpsMediaOwnerController,
    private readonly environment: OpsVoiceEnvironment = createBrowserVoiceEnvironment(),
    private readonly realtimeBridge: OpsRealtimeConversationBridge = {
      history: () => [],
      present: () => undefined
    },
    private readonly clientFreshness: OpsClientFreshnessGate | null = null
  ) {
    this.currentState = { ...INITIAL_STATE, supported: supportsVoice(environment) };
  }

  readonly getSnapshot = (): OpsVoiceState => this.currentState;
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  mount(): void {
    if (this.mounted) return;
    this.mounted = true;
    this.unregisterVoiceDisarm = this.mediaOwner.registerVoiceDisarm(() => {
      this.disarmForMeeting();
    });
  }

  unmount(): void {
    if (!this.mounted) return;
    this.invalidate(true);
    this.unregisterVoiceDisarm?.();
    this.unregisterVoiceDisarm = null;
    this.mounted = false;
    // Unmount must fence a late status read without cancelling server work.
    // Returning to the app can recover that work from authenticated history;
    // this controller instance must never resurrect it after losing ownership.
    this.retainedCanonicalTurnId = null;
    this.clearRetainedCanonicalPoll();
  }

  activateVoice = (): void => {
    if (!this.mounted) return;
    if (!this.admitHistory()) return;
    if (this.realtimePresentationUnavailable && this.activeBrainTurnId) return;
    if (this.currentState.mode === "voice") {
      // WHY [BOL-VOICE-ACTIVE-TAP-IDEMPOTENT-001]: after Stop the selected
      // Voice segment is already listening. Reopening the persistent session
      // discarded speech during its new provider handshake—the exact physical
      // "Stop, tap Voice, steer" failure. Rearm the one capture graph in place;
      // a tap while admission is still opening is an idempotent no-op. Only the
      // explicit paused-retry state may mint a replacement session.
      // Falsifier: an active Voice tap opens session two, closes session one,
      // or drops the first steering words while reconnecting.
      if (this.realtimeSession?.isReady()) {
        this.realtimeSession.setInputMuted(this.currentState.muted, true);
        return;
      }
      if (this.realtimeOpenPromise) return;
      this.activateVoiceOnCurrentClient();
      return;
    }
    // WHY [BOL-VOICE-MIC-READINESS-001]: the user needs immediate truthful
    // feedback from the physical Voice press, including while installed-client
    // freshness still owns no microphone. Response phase, elapsed time, socket
    // readiness, and the Orb cannot prove that the browser capture graph is
    // ready. Only microphone-start may promote this state to ready.
    // Falsifier: the press is visually silent, a socket/answer marks the mic
    // ready, a paused retry spins, or a physical capture failure stays green.
    this.publish({ micReadiness: "arming" });
    // Freshness checks gate only a new Text -> Voice boundary.
    if (!this.clientFreshness) {
      this.activateVoiceOnCurrentClient();
      return;
    }
    const request = ++this.activationRequest;
    const generation = this.generation;
    void this.clientFreshness.check().then((result) => {
      if (!this.mounted || request !== this.activationRequest || generation !== this.generation) return;
      if (result === "current" || result === "unavailable") {
        this.activateVoiceOnCurrentClient();
      } else if (result === "stale_blocked") {
        this.failToText("Realtime Voice could not start safely. Text still works normally.", "client_freshness_rejected");
      }
    }).catch(() => {
      if (this.mounted && request === this.activationRequest && generation === this.generation) {
        this.activateVoiceOnCurrentClient();
      }
    });
  };

  private activateVoiceOnCurrentClient(): void {
    if (!this.mounted) return;
    if (!this.admitHistory()) return;
    const carryMuted = this.currentState.mode === "voice" && this.currentState.muted;
    const generation = this.invalidate(false);
    this.currentTurnId = createOpsVoiceTurnId();
    this.finalTranscriptAt = 0;
    if (!supportsVoice(this.environment)) {
      this.failToText("Voice is not supported in this browser. Text still works normally.", "activation_unsupported");
      return;
    }
    if (!this.mediaOwner.claim("voice")) {
      this.failToText("Meeting capture is using the microphone. Stop it before starting Voice.", "activation_microphone_busy");
      return;
    }
    this.publish({
      mode: "voice",
      micReadiness: "arming",
      phase: "voice_arming",
      status: "Opening the microphone…",
      transcript: "",
      pulseIntensity: 0,
      muted: carryMuted,
      error: null,
      turnId: this.currentTurnId,
      answerLatencyMs: null,
      firstAudioLatencyMs: null,
      firstInterimLatencyMs: null,
      finalizationLatencyMs: null,
      transportElapsedMs: null,
      speechRetryAvailable: false
    });
    this.record("voice_activated");
    // WHY [BOL-VOICE-REALTIME-ONLY-001]: the accepted Voice contract has one
    // raw-PCM session and one native renderer. Keeping an injectable Web Speech
    // branch in this shipped controller left a second recognizer, answer path,
    // and TTS renderer available to production bytes even though the browser
    // binding selected realtime. Falsifier: this controller accepts a legacy
    // transcript callback, invokes browser speech output, or downgrades after a
    // realtime failure instead of failing closed to Text.
    this.startRealtime(generation, this.currentTurnId, true);
  }

  isRealtimeReady = (): boolean => this.currentState.mode === "voice"
    && Boolean(this.realtimeSession?.isReady());

  toggleMute = (): void => {
    if (!this.mounted || this.currentState.mode !== "voice") return;
    if (this.realtimePresentationUnavailable) return;
    const muted = !this.currentState.muted;
    if (this.realtimeSession && !this.realtimeSession.setInputMuted(muted, true)) {
      this.failToText("Realtime Voice stopped. Text still works normally.", "toggle_mute_rejected");
      return;
    }
    // WHY [BOL-VOICE-MUTE-CONTROLLER-001]: mute is a reversible input gate on
    // the one persistent Live session, not a mode switch or transport reset.
    // Publish only after the current session accepts the gate; while admission
    // is still opening, retain the requested state and apply it before exposing
    // the resolved session. Falsifier: mute closes/reopens Voice, changes output
    // or context, lies about a rejected gate, or unmute cannot resume capture.
    this.publish({ muted });
  };

  submitRealtimeText = (text: string): boolean => {
    if (!this.admitHistory()) return false;
    const normalized = text.replace(/\s+/gu, " ").trim();
    const session = this.realtimeSession;
    if (!normalized || !this.mounted || !session?.isReady() || this.currentState.mode !== "voice") return false;
    this.cancelRetainedCanonicalTurn();
    const turnId = createOpsVoiceTurnId();
    const staleTurnId = this.activeAudibleTurnId ?? this.activeBrainTurnId;
    this.lockPresentationPrivacy(turnId);
    if (staleTurnId && staleTurnId !== turnId) {
      session.clearOutput(staleTurnId, "explicit_stop");
    }
    if (!session.sendText({ turnId, text: normalized })) {
      this.releasePresentationPrivacy(turnId);
      return false;
    }
    this.currentTurnId = turnId;
    this.activeBrainTurnId = turnId;
    this.finalTranscriptAt = this.now();
    this.latestNativeAnswer = null;
    this.realtimeBridge.present({ type: "input-final", turnId, transcript: normalized, source: "typed" });
    this.publish({
      mode: "voice",
      phase: "thinking",
      status: "Luna is responding…",
      transcript: normalized,
      turnId,
      answerLatencyMs: null,
      firstAudioLatencyMs: null,
      error: null,
      speechRetryAvailable: false
    });
    this.record("brain_request_started", "ok", "canonical_ops_agent_via_live_typed");
    return true;
  };

  selectText = (): void => {
    if (!this.mounted) return;
    if (this.realtimePresentationUnavailable && this.activeBrainTurnId) {
      this.realtimeBridge.present({ type: "interrupted", turnId: this.activeBrainTurnId });
    }
    this.recordInterruptionIfActive("text_selected");
    this.invalidate(true);
    this.publish({
      mode: "text",
      micReadiness: "idle",
      phase: "text_idle",
      // WHY [BOL-VOICE-STATUS-NOT-MODE-001]: the selected segment already says
      // Text. Repeating “Text mode” in the adjacent status created the exact
      // text-on-text failure on desktop and phone; this line reports readiness.
      status: "Luna is ready.",
      transcript: "",
      pulseIntensity: 0,
      muted: false,
      error: null,
      turnId: null,
      answerLatencyMs: null,
      firstAudioLatencyMs: null,
      firstInterimLatencyMs: null,
      finalizationLatencyMs: null,
      transportElapsedMs: null,
      speechRetryAvailable: false
    });
  };

  stopAndListen = (): void => {
    if (!this.mounted) return;
    this.cancelRetainedCanonicalTurn();
    if (this.realtimePresentationUnavailable) {
      // This explicit Stop may close the retained result channel. A transport
      // failure itself must never take this cancellation path.
      this.selectText();
      return;
    }
    if (this.mediaOwner.owner === "meeting") {
      this.failToText("Meeting capture is using the microphone. Stop it before starting Voice.", "stop_microphone_busy");
      return;
    }
    if (this.realtimeSession && this.currentState.mode === "voice") {
      const outputTurnId = this.activeAudibleTurnId ?? this.activeBrainTurnId ?? undefined;
      this.recordInterruptionIfActive("stop_listen");
      this.activeBrainTurnId = null;
      this.activeAudibleTurnId = null;
      this.pausedOutputTurnId = null;
      this.clearRealtimeOutputResumeTimer();
      // WHY [BOL-VOICE-STREAM-GAP-CANCEL-001]: Stop can land after one Web
      // Audio source ends but before the next Gemini PCM packet arrives. The
      // controller's active brain turn is still the server cancellation owner;
      // retain it across that packet gap and pass it before clearing local UI.
      // Falsifier: Stop emits no target, then a later packet from the same answer
      // resumes speech or overlaps the newly rearmed microphone.
      this.realtimeSession.clearOutput(outputTurnId, "explicit_stop");
      this.clearPresentationPrivacyLocks();
      // Stop is already authoritative locally; do not wait for a websocket
      // acknowledgement to wake an Android capture context. Reuse the exact
      // mic graph/session and reset only its input gate/VAD state.
      this.realtimeSession.setInputMuted(this.currentState.muted, true);
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Stopped. Listening now…",
        transcript: "",
        pulseIntensity: 0,
        error: null,
        speechRetryAvailable: false
      });
      return;
    }
    // A failed/closing Live socket must never fall through to another speech
    // transport. Re-enter the one authenticated PCM session or fail closed.
    this.activateVoice();
  };

  answerSpokenApproval = (): void => {
    if (!this.mounted || !this.currentState.approvalConsentPending) return;
    if (!this.realtimeSession?.armApprovalConsent()) {
      this.publish({
        approvalConsentPending: false,
        status: "That approval question expired. Ask Luna to read the current approval again."
      });
      return;
    }
    this.publish({
      mode: "voice",
      phase: "listening",
      status: "Listening for one clear yes or no…",
      transcript: "",
      approvalConsentPending: false,
      error: null
    });
  };

  private disarmForMeeting(): void {
    this.recordInterruptionIfActive("meeting_claimed_microphone");
    this.invalidate(true);
    this.publish({
      mode: "text",
      micReadiness: "idle",
      phase: "text_idle",
      status: "Voice paused for meeting recording.",
      transcript: "",
      pulseIntensity: 0,
      muted: false,
      error: null,
      turnId: null,
      answerLatencyMs: null,
      firstAudioLatencyMs: null,
      firstInterimLatencyMs: null,
      finalizationLatencyMs: null,
      transportElapsedMs: null,
      speechRetryAvailable: false
    });
  }

  private startRealtime(generation: number, firstTurnId: string, announceReady = false): void {
    // The controller also owns expiry recovery, not just the Voice button.
    // Never open a new session using [] while authorized history is pending.
    if (!this.admitHistory()) return;
    const openRealtimeSession = this.environment.openRealtimeSession;
    const publicReleaseId = this.realtimeBridge.publicReleaseId?.() ?? null;
    if (!openRealtimeSession || !publicReleaseId || !this.isCurrent(generation)) {
      this.handleRealtimeStartFailure(
        new Error(realtimeUnsupportedMessage(this.environment.realtimeUnsupportedReason)),
        generation
      );
      return;
    }
    let opening: Promise<OpsRealtimeVoiceSession>;
    try {
      opening = openRealtimeSession({
        publicReleaseId,
        firstTurnId,
        history: this.realtimeBridge.history(),
        getWorkspaceContext: () => this.realtimeBridge.workspaceContext?.() ?? null,
        getPresentationPrivacy: () => this.realtimeBridge.presentationPrivacy?.() ?? "MASK_CONTACT_DETAILS",
        // WHY [BOL-VOICE-READY-WELCOME-OWNER-001]: only the deliberate Voice
        // switch owns the one audible welcome. Expiry recovery and socket resume
        // must retain the conversation without repeatedly greeting the user.
        // Falsifier: a recovery greets again, or the initial successful tap is silent.
        announceReady,
        onEvent: (event) => this.receiveRealtimeEvent(event, generation)
      });
    } catch (error) {
      this.handleRealtimeStartFailure(error, generation);
      return;
    }
    this.realtimeOpenPromise = opening;
    void opening.then((session) => {
      if (!this.admitHistory()) {
        session.close();
        return;
      }
      if (!this.isCurrent(generation) || this.realtimeOpenPromise !== opening) {
        session.close();
        return;
      }
      this.realtimeOpenPromise = null;
      this.realtimeSession = session;
      if (this.currentState.muted && !session.setInputMuted(true)) {
        this.failToText("Realtime Voice stopped. Text still works normally.", "opened_mute_rejected");
        return;
      }
      if (session.isReady() && this.currentState.phase === "voice_arming") {
        this.publish({ phase: "listening", status: "Listening · realtime Luna Voice.", error: null });
      }
    }).catch((error) => {
      if (!this.isCurrent(generation) || this.realtimeOpenPromise !== opening) return;
      this.realtimeOpenPromise = null;
      this.handleRealtimeStartFailure(error, generation);
    });
  }

  private handleRealtimeStartFailure(error: unknown, generation: number): void {
    if (!this.isCurrent(generation)) return;
    if (this.realtimeExpiryRecoveryGeneration === generation) {
      this.pauseRealtimeForTrustedRetry(error);
      return;
    }
    this.failToText(realtimeStartErrorMessage(error), "realtime_open_rejected");
  }

  private recoverExpiredRealtimeSession(generation: number): void {
    if (!this.isCurrent(generation)) return;
    if (this.realtimeExpiryRecoveryGeneration === generation) {
      this.pauseRealtimeForTrustedRetry(new Error("fresh realtime admission expired"));
      return;
    }
    const retainedTurnId = this.retainedCanonicalTurnId ?? this.activeBrainTurnId;
    // WHY [BOL-VOICE-LEASE-IS-NOT-WORK-001]: renewal retires the old ticket,
    // microphone/audio and spoken consent, not the already-admitted Chat row.
    // Recover only its authenticated signed final, never reissue the request or
    // feed a final to the fresh speech renderer. Stop/new prompt cancels by the
    // same member-qualified operation identity even after the old socket closes.
    this.retainedCanonicalTurnId = retainedTurnId;
    this.record("realtime_reconnecting", "blocked", "session_expired_fresh_admission");
    // WHY [BOL-VOICE-BOUNDED-SESSION-REFRESH-001]: the server's 30-minute
    // session bound is security authority, not a reason to switch the selected
    // mode or retry a dead ticket. Fence the retired generation, seed exactly
    // one new Ops-owned Luna session from current canonical history, and retain
    // the already-claimed Voice owner so the authorized microphone can restart.
    // A rejected replacement releases the mic and leaves one visible trusted
    // Listen action; never mint in a loop or enter legacy Text/Speech paths.
    // Falsifier: expiry opens zero or two fresh sessions, loses history, reuses
    // the old ticket, switches to Text, or retries again without a user gesture.
    const recoveryGeneration = this.invalidate(false);
    this.realtimeExpiryRecoveryGeneration = recoveryGeneration;
    this.currentTurnId = createOpsVoiceTurnId();
    this.finalTranscriptAt = 0;
    this.publish({
      mode: "voice",
      micReadiness: "arming",
      phase: "voice_arming",
      status: "Refreshing realtime Luna Voice…",
      transcript: "",
      pulseIntensity: 0,
      error: null,
      turnId: this.currentTurnId,
      answerLatencyMs: null,
      firstAudioLatencyMs: null,
      firstInterimLatencyMs: null,
      finalizationLatencyMs: null,
      transportElapsedMs: null,
      speechRetryAvailable: false,
      approvalConsentPending: false
    });
    this.startRealtime(recoveryGeneration, this.currentTurnId, false);
    if (retainedTurnId) {
      this.activeBrainTurnId = retainedTurnId;
      this.pollRetainedCanonicalTurn(retainedTurnId);
    }
  }

  private clearRetainedCanonicalPoll(): void {
    if (this.retainedCanonicalPoll !== null) this.environment.clearTimer(this.retainedCanonicalPoll);
    this.retainedCanonicalPoll = null;
  }

  private pollRetainedCanonicalTurn(turnId: string): void {
    if (!this.mounted || this.retainedCanonicalTurnId !== turnId || !this.environment.readTurnStatus) return;
    this.clearRetainedCanonicalPoll();
    void this.environment.readTurnStatus(turnId).then((status) => {
      if (!this.mounted || this.retainedCanonicalTurnId !== turnId || status.turnId !== turnId) return;
      if (status.outcome === "COMPLETED" && status.publicTurn?.turnId === turnId) {
        this.realtimeBridge.present({ type: "public-turn", turnId, publicTurn: status.publicTurn });
        this.retainedCanonicalTurnId = null;
        if (this.activeBrainTurnId === turnId) this.activeBrainTurnId = null;
        this.releasePresentationPrivacy(turnId);
        if (this.currentState.turnId === turnId || !this.activeBrainTurnId) {
          this.realtimePresentationUnavailable = false;
          this.publish({
            phase: this.currentState.mode === "voice" ? "listening" : "text_idle",
            status: "Luna is ready.", pulseIntensity: 0, error: null
          });
        }
        return;
      }
      if (status.outcome === "NOT_ACTIVE") {
        this.retainedCanonicalTurnId = null;
        if (this.activeBrainTurnId === turnId) this.activeBrainTurnId = null;
        const message = "The app couldn't confirm whether Luna's result was saved. Try the same request again.";
        this.realtimeBridge.present({ type: "failed", turnId, message });
        if (this.currentState.mode === "text" && this.realtimePresentationUnavailable) {
          this.realtimePresentationUnavailable = false;
          this.publish({ phase: "error", status: message, pulseIntensity: 0, error: message });
        }
        return;
      }
      this.retainedCanonicalPoll = this.environment.setTimer(() => this.pollRetainedCanonicalTurn(turnId), 3_000);
    }).catch((error) => {
      if (!this.mounted || this.retainedCanonicalTurnId !== turnId) return;
      // Never poll through revoked authentication or disclose a retained result
      // under a different account. Transient transport loss keeps the same row.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        this.retainedCanonicalTurnId = null;
        if (this.activeBrainTurnId === turnId) this.activeBrainTurnId = null;
        return;
      }
      this.retainedCanonicalPoll = this.environment.setTimer(() => this.pollRetainedCanonicalTurn(turnId), 3_000);
    });
  }

  cancelRetainedCanonicalTurn = (): void => {
    const turnId = this.retainedCanonicalTurnId;
    if (!turnId) return;
    this.clearRetainedCanonicalPoll();
    this.retainedCanonicalTurnId = null;
    if (this.activeBrainTurnId === turnId) this.activeBrainTurnId = null;
    void this.environment.cancelTurn?.(turnId).then(async (receipt) => {
      if (!this.mounted || receipt.turnId !== turnId) return;
      if (receipt.outcome === "CANCELLED") {
        this.realtimeBridge.present({ type: "interrupted", turnId });
      } else {
        const status = await this.environment.readTurnStatus?.(turnId);
        if (this.mounted && status?.turnId === turnId && status.publicTurn?.turnId === turnId) {
          this.realtimeBridge.present({ type: "public-turn", turnId, publicTurn: status.publicTurn });
        }
      }
    }).catch(() => {
      // WHY [BOL-VOICE-RETAINED-CANCEL-FENCE-001]: explicit Stop/new input is
      // local presentation authority even if the cancellation receipt is lost.
      // Restoring the old poll in this catch could paint its late final over the
      // newer intent. Server history remains authoritative on a later reload;
      // this retired controller operation must not resurrect the row itself.
    });
  };

  private pauseRealtimeForTrustedRetry(error: unknown): void {
    this.record("voice_error", "blocked", safeVoiceErrorDetail(realtimeStartErrorMessage(error)));
    this.invalidate(true);
    this.currentTurnId = null;
    this.publish({
      mode: "voice",
      micReadiness: "paused",
      phase: "voice_arming",
      status: "Voice paused. Tap Listen to reconnect safely.",
      transcript: "",
      pulseIntensity: 0,
      error: null,
      turnId: null,
      answerLatencyMs: null,
      firstAudioLatencyMs: null,
      firstInterimLatencyMs: null,
      finalizationLatencyMs: null,
      transportElapsedMs: null,
      speechRetryAvailable: false,
      approvalConsentPending: false
    });
  }

  private receiveRealtimeEvent(event: OpsRealtimeVoiceEvent, generation: number): void {
    if (!this.isCurrent(generation)) return;
    if (!this.admitHistory()) return;
    if (event.type === "session-expired") {
      this.recoverExpiredRealtimeSession(generation);
      return;
    }
    if (event.type === "microphone-start") {
      this.publish({
        micReadiness: "ready",
        phase: "voice_arming",
        status: "Microphone open · connecting realtime Luna Voice…",
        error: null
      });
      this.record("microphone_started");
      return;
    }
    if (event.type === "ready") {
      if (this.realtimeExpiryRecoveryGeneration === generation) {
        this.realtimeExpiryRecoveryGeneration = null;
      }
      const retainedTurnPhase = this.activeAudibleTurnId
        ? "speaking"
        : this.activeBrainTurnId ? "thinking" : "listening";
      const retainedTurnStatus = this.activeAudibleTurnId
        ? "Luna is speaking."
        : this.activeBrainTurnId
          ? "Luna is responding…"
          : event.resumed
            ? "Listening again · realtime Luna Voice."
            : "Listening · realtime Luna Voice.";
      // WHY [BOL-VOICE-READY-ACTIVE-TURN-001]: a resumed socket is transport
      // readiness, not proof that the still-pending Luna turn ended. Publishing
      // Listening unconditionally produced the literal frozen phone state: the
      // inline row kept working while the main Orb went idle. Preserve the
      // active brain/audible owner; only a truly ownerless session may listen.
      // Falsifier: ready(resumed=true) changes a pending turn from Thinking to
      // Listening or makes its main Orb disagree with its inline work state.
      this.publish({
        phase: retainedTurnPhase,
        status: retainedTurnStatus,
        approvalConsentPending: false,
        error: null
      });
      this.record("realtime_ready", "ok", event.resumed ? "resumed" : "fresh");
      return;
    }
    if (event.type === "reconnecting") {
      // WHY [BOL-VOICE-RECONNECT-UI-001]: reconnect revokes cues/approval and
      // retains the canonical operation. It may retain an audible owner only
      // when the browser explicitly preserved that already-active completed
      // answer's receiver tail; the neutral ID never invents a heard start.
      // Falsifier: a cut cue/ack restores Speaking, a mismatched/newer owner is
      // claimed, or a preserved tail returns from ready as dead Thinking.
      if (!event.preservedOutputTurnId
        || this.activeAudibleTurnId !== event.preservedOutputTurnId) {
        this.activeAudibleTurnId = null;
      }
      this.pausedOutputTurnId = null;
      this.clearRealtimeOutputResumeTimer();
      this.publish({
        mode: "voice",
        phase: "voice_arming",
        status: "Reconnecting realtime Luna Voice…",
        approvalConsentPending: false,
        error: null
      });
      this.record("realtime_reconnecting", "blocked", `attempt_${event.attempt}`);
      return;
    }
    if (event.type === "approval-consent-required") {
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Tap Answer approval, then say one clear yes or no.",
        approvalConsentPending: true,
        error: null
      });
      this.record("approval_consent_required", "ok", "foreground_required");
      return;
    }
    if (event.type === "approval-consent-armed") {
      this.currentTurnId = event.turnId;
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Listening for one clear yes or no…",
        transcript: "",
        turnId: event.turnId,
        approvalConsentPending: false,
        error: null
      });
      this.record("approval_consent_armed", "ok", "foreground_gesture");
      return;
    }
    if (event.type === "approval-consent-result") {
      this.publish({
        phase: "thinking",
        status: event.outcome === "approved"
          ? "Approved exactly one action. Luna is confirming…"
          : event.outcome === "declined"
            ? "Nothing approved. Luna is confirming…"
            : "Nothing approved. Luna is responding…",
        approvalConsentPending: false,
        error: null
      });
      this.record("approval_consent_result", "ok", event.outcome);
      return;
    }
    if (event.type === "no-speech") {
      if (this.currentTurnId === event.turnId) this.currentTurnId = null;
      if (this.activeBrainTurnId === event.turnId) this.activeBrainTurnId = null;
      // WHY [BOL-VOICE-PRIVATE-SETTLEMENT-OWNER-001]: local capture can start B
      // while the provider is still draining private no-speech turn A. A's
      // receipt retires only A; resetting the shared display here erased B's
      // "Hearing you" state despite its PCM still being captured correctly.
      // Falsifier: activity-start(B) then no-speech(A) changes B's visible turn.
      if (this.currentTurnId !== null || this.activeBrainTurnId !== null
        || this.activeAudibleTurnId !== null) return;
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Listening · realtime Luna Voice.",
        transcript: "",
        turnId: null,
        pulseIntensity: 0,
        error: null,
        approvalConsentPending: false
      });
      this.record("listening_rearmed", "ok", "private_no_speech");
      return;
    }
    if (event.type === "activity-start") {
      const sameTurn = this.currentTurnId === event.turnId;
      const candidateDuringActiveResponse = Boolean(
        (this.activeAudibleTurnId ?? this.activeBrainTurnId)
          && (this.activeAudibleTurnId ?? this.activeBrainTurnId) !== event.turnId
      );
      this.realtimeActivityStartedAt.set(event.turnId, event.elapsedMs);
      this.realtimeActivityEndedAt.delete(event.turnId);
      const earlyPartialAt = this.realtimeFirstPartialAt.get(event.turnId);
      if (!sameTurn) {
        this.realtimeInterimSeen.delete(event.turnId);
        this.realtimeExplicitHoldInterimSeen.delete(event.turnId);
      }
      if (this.realtimeInterimSeen.has(event.turnId)) this.clearRealtimeOutputResumeTimer();
      // WHY [BOL-VOICE-WORD-GATED-BARGE-001]: acoustic energy is not intent.
      // Speaker echo, a clap, or room noise must leave Luna's playback clock
      // untouched. Only a non-empty provider transcript below may pause the
      // exact tail; its final meaning alone may resume, hold, replace, or stop.
      // Falsifier: transcript-free activity suspends output, changes its owner,
      // or delays a resume that was already authorized by recognized words.
      if (candidateDuringActiveResponse) {
        return;
      }
      this.lockPresentationPrivacy(event.turnId);
      this.currentTurnId = event.turnId;
      if (this.latestNativeAnswer?.turnId !== event.turnId) this.latestNativeAnswer = null;
      this.finalTranscriptAt = 0;
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Hearing you…",
        transcript: sameTurn ? this.currentState.transcript : "",
        pulseIntensity: 0,
        error: null,
        turnId: event.turnId,
        answerLatencyMs: null,
        firstAudioLatencyMs: null,
        firstInterimLatencyMs: earlyPartialAt === undefined
          ? sameTurn ? this.currentState.firstInterimLatencyMs : null
          : Math.max(0, Math.round(earlyPartialAt - event.elapsedMs)),
        finalizationLatencyMs: null,
        transportElapsedMs: null,
        speechRetryAvailable: false,
        approvalConsentPending: false
      });
      this.record("activity_started");
      return;
    }
    if (event.type === "activity-end") {
      this.realtimeActivityEndedAt.set(event.turnId, event.elapsedMs);
      if (this.pausedOutputTurnId
        && this.realtimeInterimSeen.has(event.turnId)
        && !this.realtimeExplicitHoldInterimSeen.has(event.turnId)) {
        this.scheduleRealtimeOutputResume(event.turnId, OPS_TRANSCRIPT_FINAL_RESUME_MS);
      }
      return;
    }
    if (event.type === "input-partial") {
      if (this.finalizedRealtimeTurns.has(event.turnId)) return;
      if (!normalizeOpsVoiceInterruption(event.transcript)) return;
      const interruptionIntent = classifyOpsVoiceInterruption(event.transcript, false);
      this.pauseOutputForPotentialInput(event.turnId);
      if (interruptionIntent === "explicit_stop") {
        // A recognized Stop/wait/hold is enough to make playback physically
        // quiet. Final Stop destroys the suffix; wait/hold may retain it until
        // final intent or a later instruction arrives.
        this.realtimeExplicitHoldInterimSeen.add(event.turnId);
        this.clearRealtimeOutputResumeTimer();
      } else if (this.pausedOutputTurnId && this.realtimeActivityEndedAt.has(event.turnId)) {
        this.realtimeExplicitHoldInterimSeen.delete(event.turnId);
        // Provider transcript and local activity are independently ordered. If
        // words arrive after speech_end, retain the same bounded final wait that
        // activity-end would have scheduled instead of stranding a paused tail.
        this.scheduleRealtimeOutputResume(event.turnId, OPS_TRANSCRIPT_FINAL_RESUME_MS);
      } else {
        this.realtimeExplicitHoldInterimSeen.delete(event.turnId);
      }
      this.currentTurnId = event.turnId;
      const patch: Partial<OpsVoiceState> = {
        mode: "voice",
        phase: "listening",
        status: "Hearing you…",
        transcript: event.transcript,
        turnId: event.turnId,
        error: null
      };
      if (!this.realtimeInterimSeen.has(event.turnId)) {
        this.realtimeInterimSeen.add(event.turnId);
        this.realtimeFirstPartialAt.set(event.turnId, event.elapsedMs);
        const startedAt = this.realtimeActivityStartedAt.get(event.turnId);
        patch.firstInterimLatencyMs = startedAt === undefined
          ? null
          : Math.max(0, Math.round(event.elapsedMs - startedAt));
        this.record("first_interim_transcript");
      } else {
        this.record("interim_transcript");
      }
      this.publish(patch);
      return;
    }
    if (event.type === "input-final") {
      if (this.retainedCanonicalTurnId && this.retainedCanonicalTurnId !== event.turnId) {
        this.cancelRetainedCanonicalTurn();
      }
      if (this.finalizedRealtimeTurns.has(event.turnId)) return;
      if (!normalizeOpsVoiceInterruption(event.transcript)) return;
      this.lockPresentationPrivacy(event.turnId);
      this.rememberFinalizedRealtimeTurn(event.turnId);
      this.realtimeExplicitHoldInterimSeen.delete(event.turnId);
      this.clearRealtimeOutputResumeTimer();
      const interruptionIntent = classifyOpsVoiceInterruption(event.transcript, true);
      const controlAction = interruptionIntent === "explicit_stop"
        ? opsVoiceControlAction(event.transcript)
        : null;
      const continuationTarget = this.realtimeContinuationTargetByTurn.get(event.turnId)
        ?? this.pausedOutputTurnId
        // WHY [BOL-VOICE-FINAL-ONLY-BARGE-001]: Gemini may deliver a short
        // control or correction only as a final transcript. Requiring an earlier
        // partial left the current answer speaking through a recognized Stop.
        // A final word-backed turn may therefore claim the exact audible owner;
        // raw activity still has no cancellation authority, and backchannels
        // take the non-destructive continuation branch below.
        // Falsifier: final-only Stop leaves the old audio playing, or noise with
        // no transcript reaches interruption.
        ?? (this.activeAudibleTurnId && this.activeAudibleTurnId !== event.turnId
          ? this.activeAudibleTurnId
          : this.activeBrainTurnId && this.activeBrainTurnId !== event.turnId
            ? this.activeBrainTurnId
          : null);
      if (controlAction === "hold") {
        this.pauseOutputForPotentialInput(event.turnId);
        this.realtimeSession?.discardOutput(event.turnId);
        this.realtimeBridge.present({
          type: "input-final",
          turnId: event.turnId,
          transcript: event.transcript,
          source: "voice"
        });
        this.realtimeBridge.present({ type: "interrupted", turnId: event.turnId });
        this.currentTurnId = event.turnId;
        this.finalTranscriptAt = this.now();
        this.publish({
          mode: "voice",
          phase: "listening",
          status: "Paused. Tell me what to change, or say continue.",
          transcript: event.transcript,
          turnId: event.turnId,
          pulseIntensity: 0,
          error: null,
          approvalConsentPending: false
        });
        // WHY [BOL-VOICE-REVERSIBLE-STEERING-001]: wait/hold must become
        // silence immediately without destroying the unheard suffix. An
        // explicit continuation resumes it; any substantive next utterance is
        // sent intact through the same Luna session and replaces it. The
        // browser never guesses semantic equivalence from arbitrary wording.
        // Falsifier: a hold receives a Luna reply, resumes before steering, or
        // loses the suffix that an explicit Continue should finish once.
        this.record("final_transcript", "ok", "reversible_hold");
        this.releasePresentationPrivacy(event.turnId);
        return;
      }
      if (continuationTarget && interruptionIntent === "backchannel") {
        this.realtimeSession?.discardOutput(event.turnId);
        this.realtimeBridge.present({
          type: "input-final",
          turnId: event.turnId,
          transcript: event.transcript,
          source: "voice"
        });
        // A backchannel is visible user context, not a second Luna request. Settle
        // its presentation row immediately while the original audible owner
        // resumes; provider-side output for this turn remains locally discarded.
        this.realtimeBridge.present({ type: "interrupted", turnId: event.turnId });
        this.resumePausedOutput(event.turnId, true);
        this.currentTurnId = event.turnId;
        this.finalTranscriptAt = this.now();
        this.publish({
          mode: "voice",
          phase: "speaking",
          status: "Luna is continuing.",
          transcript: event.transcript,
          turnId: event.turnId,
          error: null
        });
        this.record("final_transcript", "ok", "backchannel_continue");
        this.releasePresentationPrivacy(event.turnId);
        return;
      }
      if (continuationTarget) this.interruptPausedOutput(event.turnId);
      if (controlAction === "cancel") {
        this.currentTurnId = event.turnId;
        this.latestNativeAnswer = null;
        this.finalTranscriptAt = this.now();
        this.realtimeBridge.present({
          type: "input-final",
          turnId: event.turnId,
          transcript: event.transcript,
          source: "voice",
          control: "explicit_stop"
        });
        // The control phrase is an input turn at the provider too. Cancel that
        // exact turn after settling its visible user row, otherwise Luna can
        // answer "Stop" and speak over the corrected request that follows.
        this.realtimeSession?.clearOutput(event.turnId, "explicit_stop");
        this.realtimeSession?.setInputMuted(this.currentState.muted);
        this.activeBrainTurnId = null;
        this.activeAudibleTurnId = null;
        this.pausedOutputTurnId = null;
        this.publish({
          mode: "voice",
          phase: "listening",
          status: "Stopped. Listening now…",
          transcript: event.transcript,
          turnId: event.turnId,
          pulseIntensity: 0,
          error: null,
          approvalConsentPending: false
        });
        this.record("final_transcript", "ok", "explicit_control_only");
        this.releasePresentationPrivacy(event.turnId);
        return;
      }
      this.currentTurnId = event.turnId;
      this.activeBrainTurnId = event.turnId;
      this.latestNativeAnswer = null;
      this.finalTranscriptAt = this.now();
      const endedAt = this.realtimeActivityEndedAt.get(event.turnId);
      this.publish({
        mode: "voice",
        phase: "thinking",
        status: "Luna is responding…",
        transcript: event.transcript,
        turnId: event.turnId,
        // Finalization starts when local VAD closes the utterance, not when the
        // user began speaking. Counting the whole utterance hid the exact
        // multi-second post-speech delay the physical-phone check must reject.
        finalizationLatencyMs: endedAt === undefined
          ? null
          : Math.max(0, Math.round(event.elapsedMs - endedAt)),
        pulseIntensity: 0,
        error: null
      });
      this.record("final_transcript");
      this.record("brain_request_started", "ok", "canonical_ops_agent_via_live_voice");
      this.realtimeBridge.present({
        type: "input-final",
        turnId: event.turnId,
        transcript: event.transcript,
        source: "voice"
      });
      return;
    }
    if (event.type === "input-revision") {
      // A provider correction may revise presentation for the same turn, but
      // final-once ownership forbids a second Luna request/action/approval.
      if (this.currentTurnId === event.turnId) this.publish({ transcript: event.transcript });
      this.realtimeBridge.present({ type: "input-revision", turnId: event.turnId, transcript: event.transcript });
      this.record("transcript_revised");
      return;
    }
    if (event.type === "context-pending") {
      if (this.activeBrainTurnId === event.turnId) {
        this.publish({ phase: "thinking", status: "Luna is responding…", transportElapsedMs: event.elapsedMs });
      }
      return;
    }
    if (event.type === "checking") {
      if (this.activeBrainTurnId !== event.turnId) return;
      // A checking receipt owns work state, not spoken wording. The exact
      // scheduled PCM event below is the sole realtime phrase publisher.
      this.publish({ phase: "thinking", transportElapsedMs: event.elapsedMs });
      return;
    }
    // WHY [BOL-VOICE-CHAT-ONLY-PRESENTATION-001]: acknowledgment, progress,
    // and status-cue text is spoken conversation and is already appended to
    // the canonical Chat turn through realtimeBridge.present. Publishing the
    // same text as controller `status` duplicated Luna's words in the composer.
    // Keep status phase-owned and short; exact presentation text remains solely
    // in Chat/audio. Errors, approval controls, readiness, Stop, and reconnect
    // retain their explicit lifecycle wording. Falsifier: any spoken phrase is
    // repeated below Chat, or a lifecycle/control status loses its meaning.
    if (event.type === "acknowledgment-audible-start") {
      if (this.activeBrainTurnId !== event.turnId) return;
      this.activeAudibleTurnId = event.turnId;
      const isFirstAudibleSpeech = this.currentState.firstAudioLatencyMs === null;
      const firstAudioLatencyMs = this.currentState.firstAudioLatencyMs ?? this.elapsedFromFinal();
      // Presentation-only: Chat appends the exact audible phrase and its
      // receiver identity receipt retains ordinary history, never canonical
      // response, model history, or public TurnResult authority.
      this.realtimeBridge.present({
        type: "acknowledgment",
        turnId: event.turnId,
        text: event.text,
        presentationId: event.presentationId,
        revision: event.revision
      });
      this.publish({
        mode: "voice",
        phase: "speaking",
        status: "Luna is speaking.",
        firstAudioLatencyMs,
        pulseIntensity: 0,
        error: null,
        speechRetryAvailable: false
      });
      if (isFirstAudibleSpeech) {
        this.record("first_audible_speech", "ok", "model_authored_acknowledgment");
      }
      return;
    }
    if (event.type === "acknowledgment-update") {
      if (this.activeBrainTurnId !== event.turnId) return;
      this.realtimeBridge.present({
        type: "acknowledgment",
        turnId: event.turnId,
        text: event.text,
        presentationId: event.presentationId,
        revision: event.revision
      });
      return;
    }
    if (event.type === "acknowledgment-audible-end") {
      if (this.activeAudibleTurnId === event.turnId) this.activeAudibleTurnId = null;
      if (this.activeBrainTurnId !== event.turnId) return;
      this.publish({
        mode: "voice",
        phase: "thinking",
        status: "Luna is responding…",
        pulseIntensity: 0,
        error: null
      });
      return;
    }
    if (event.type === "model-progress") {
      if (this.activeBrainTurnId !== event.turnId) return;
      this.realtimeBridge.present({
        type: "model-progress",
        turnId: event.turnId,
        text: event.transcript
      });
      this.publish({
        mode: "voice",
        phase: "speaking",
        status: "Luna is speaking.",
        transportElapsedMs: event.elapsedMs,
        error: null
      });
      return;
    }
    if (event.type === "model-progress-audible-end") {
      if (this.activeBrainTurnId !== event.turnId) return;
      this.publish({
        mode: "voice",
        phase: "thinking",
        status: "Luna is continuing…",
        transportElapsedMs: event.elapsedMs,
        error: null
      });
      return;
    }
    if (event.type === "status-cue") {
      const ownsTurn = this.activeBrainTurnId === event.turnId
        || (!this.activeBrainTurnId && this.currentTurnId === event.turnId);
      if (!ownsTurn) return;
      this.realtimeBridge.present({
        type: "status-cue",
        turnId: event.turnId,
        text: event.text,
        ...(event.cueId ? { cueId: event.cueId } : {}),
        ...(event.presentationId ? { presentationId: event.presentationId } : {})
      });
      this.publish({
        phase: this.activeBrainTurnId === event.turnId ? "thinking" : this.currentState.phase,
        status: this.activeBrainTurnId === event.turnId
          ? "Luna is responding…"
          : "Listening · realtime Luna Voice.",
        transportElapsedMs: event.elapsedMs
      });
      return;
    }
    if (event.type === "context-accepted") {
      if (this.activeBrainTurnId === event.turnId) {
        this.publish({ phase: "thinking", status: "Luna is responding…", transportElapsedMs: event.elapsedMs });
      }
      return;
    }
    if (event.type === "public-turn") {
      const recovered = event.recovered === true;
      const failedAudioTurn = this.recoverableAudioFailedTurns.has(event.turnId);
      if (!recovered && this.activeBrainTurnId !== event.turnId && !failedAudioTurn) return;
      const paintReceipt = this.realtimeBridge.present({
        type: "public-turn",
        turnId: event.turnId,
        publicTurn: event.publicTurn
      });
      if (this.realtimePresentationUnavailable) {
        // Canonical text/proofs do not depend on an audible final. Keep this
        // exact owner until completion (or replay), never show a Retry failure
        // or start another renderer/model for an already-running answer.
        void Promise.resolve(paintReceipt).then((painted) => {
          if (painted === true) this.markAnswerVisible(generation, event.turnId);
        }).catch(() => undefined);
        if (recovered) {
          this.activeBrainTurnId = null;
          this.selectText();
        }
        return;
      }
      if (recovered) {
        const ownsTurn = this.activeBrainTurnId === event.turnId
          || this.activeAudibleTurnId === event.turnId
          || this.pausedOutputTurnId === event.turnId;
        this.releasePresentationPrivacy(event.turnId);
        this.recoverableAudioFailedTurns.delete(event.turnId);
        if (this.latestNativeAnswer?.turnId === event.turnId) this.latestNativeAnswer = null;
        if (this.activeBrainTurnId === event.turnId) this.activeBrainTurnId = null;
        if (this.activeAudibleTurnId === event.turnId) this.activeAudibleTurnId = null;
        if (this.pausedOutputTurnId === event.turnId) this.pausedOutputTurnId = null;
        void Promise.resolve(paintReceipt).catch(() => undefined);
        if (ownsTurn) {
          this.realtimeSession?.setInputMuted(this.currentState.muted);
          this.publish({
            mode: "voice",
            phase: "listening",
            status: "Listening again…",
            transcript: "",
            pulseIntensity: 0,
            error: null,
            approvalConsentPending: false
          });
        }
        return;
      }
      if (failedAudioTurn) {
        return;
      }
      this.publish({ phase: "thinking", status: "Answer ready · preparing voice…" });
      this.record("brain_heartbeat", "ok", "release_bound_public_turn");
      void Promise.resolve(paintReceipt).then((painted) => {
        if (painted === true) this.markAnswerVisible(generation, event.turnId);
      }).catch(() => undefined);
      return;
    }
    if (event.type === "assistant-partial") {
      const failedAudioTurn = this.recoverableAudioFailedTurns.has(event.turnId);
      if (this.activeBrainTurnId !== event.turnId && !failedAudioTurn) return;
      if (failedAudioTurn) {
        this.realtimeBridge.present({ type: "assistant-partial", turnId: event.turnId, transcript: event.transcript });
        return;
      }
      this.realtimeBridge.present({ type: "assistant-partial", turnId: event.turnId, transcript: event.transcript });
      // WHY [BOL-VOICE-PHASE-PCM-OWNERSHIP-001]: Gemini output transcription
      // is independently ordered and can arrive after audible PCM has started.
      // The physical speaker clock, not a late text revision, owns the visible
      // phase until audible-end/interruption/error. Falsifier: the Orb/status
      // says Thinking or waiting while Luna is audibly speaking.
      if (this.currentState.phase !== "speaking") {
        this.publish({ phase: "thinking", status: "Luna is responding…" });
      }
      return;
    }
    if (event.type === "assistant-final") {
      const failedAudioTurn = this.recoverableAudioFailedTurns.has(event.turnId);
      if (this.activeBrainTurnId !== event.turnId && !failedAudioTurn) return;
      if (failedAudioTurn) {
        // Output transcription and PCM are independently ordered. Preserve the
        // exact failed turn's late canonical words in Chat without reviving its
        // discarded audio, changing the current Voice phase, or claiming a
        // newer turn. The bounded ownership set is cleared with the session.
        // Falsifier: error-before-final leaves a failure placeholder, speaks a
        // second voice, or a late old final changes the active turn's status.
        this.realtimeBridge.present({ type: "assistant-final", turnId: event.turnId, transcript: event.transcript });
        return;
      }
      this.latestNativeAnswer = { text: event.transcript, generation, turnId: event.turnId };
      this.realtimeBridge.present({ type: "assistant-final", turnId: event.turnId, transcript: event.transcript });
      if (this.currentState.phase !== "speaking") {
        this.publish({ phase: "thinking", status: "Answer ready · waiting for audio…" });
      }
      return;
    }
    if (event.type === "assistant-audio-start") {
      if (this.activeBrainTurnId === event.turnId) {
        this.publish({ phase: "thinking", status: "Answer ready · starting audio…" });
        this.record("assistant_audio_started");
      }
      return;
    }
    if (event.type === "assistant-audible-start") {
      if (this.activeBrainTurnId !== event.turnId) return;
      this.activeAudibleTurnId = event.turnId;
      const isFirstAudibleSpeech = this.currentState.firstAudioLatencyMs === null;
      const firstAudioLatencyMs = this.currentState.firstAudioLatencyMs ?? this.elapsedFromFinal();
      this.publish({
        mode: "voice",
        phase: "speaking",
        status: "Luna is speaking.",
        firstAudioLatencyMs,
        pulseIntensity: 0,
        error: null,
        speechRetryAvailable: false
      });
      if (isFirstAudibleSpeech) {
        this.record("first_audible_speech", "ok", "streamed_pcm");
      }
      return;
    }
    if (event.type === "assistant-audible-end") {
      this.releasePresentationPrivacy(event.turnId);
      if (this.activeAudibleTurnId !== event.turnId && this.activeBrainTurnId !== event.turnId) return;
      this.record("speech_ended", "ok", "streamed_pcm");
      if (this.latestNativeAnswer?.turnId === event.turnId) this.latestNativeAnswer = null;
      if (this.activeAudibleTurnId === event.turnId) this.activeAudibleTurnId = null;
      // WHY [BOL-VOICE-STALE-AUDIBLE-OWNER-001]: clearing an old Web Audio
      // source is asynchronous. Its physical end may arrive after recognized
      // words have already made the replacement turn Luna's active owner. The
      // retired source may release only its own audible lease; rearming the
      // whole controller here erased the newer brain owner, froze its answer,
      // hid the main Orb scan, and ignored its later response packets.
      // Falsifier: turn-one audible-end changes turn-two Thinking to Listening
      // or prevents turn-two status/answer presentation.
      if (this.activeBrainTurnId && this.activeBrainTurnId !== event.turnId) return;
      this.activeBrainTurnId = null;
      this.realtimeSession?.setInputMuted(this.currentState.muted);
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Listening again…",
        transcript: "",
        pulseIntensity: 0,
        error: null
      });
      this.record("listening_rearmed", "ok", "continuous_pcm");
      return;
    }
    if (event.type === "turn-complete" || event.type === "turn-output-settled") {
      if (this.realtimePresentationUnavailable && this.activeBrainTurnId === event.turnId) {
        this.activeBrainTurnId = null;
        this.selectText();
        return;
      }
      if (event.type === "turn-complete" && this.activeBrainTurnId === event.turnId) {
        this.record("brain_heartbeat", "ok", "persistent_live_turn_complete");
      }
      this.realtimeSession?.setInputMuted(this.currentState.muted);
      if (event.type === "turn-complete" && event.awaitingAudibleEnd) return;
      this.releasePresentationPrivacy(event.turnId);
      const ownsTurn = this.activeBrainTurnId === event.turnId
        || this.activeAudibleTurnId === event.turnId
        || this.pausedOutputTurnId === event.turnId;
      if (!ownsTurn) return;
      if (this.latestNativeAnswer?.turnId === event.turnId) this.latestNativeAnswer = null;
      if (this.activeBrainTurnId === event.turnId) this.activeBrainTurnId = null;
      if (this.activeAudibleTurnId === event.turnId) this.activeAudibleTurnId = null;
      if (this.pausedOutputTurnId === event.turnId) this.pausedOutputTurnId = null;
      // WHY [BOL-VOICE-NO-AUDIO-SETTLEMENT-001]: refusals and valid no-audio
      // Live turns can complete without ever acquiring an audible source. The
      // old controller waited only for assistant-audible-end, so Chat finished
      // while the Orb stayed Thinking and capture had no lifecycle action to
      // wake it. Settle only the exact completed owner; a scheduled answer
      // retains audible-end ownership. Falsifier: a refusal requires a mute
      // cycle, a stale receipt rearms over a newer turn, or completion cuts
      // scheduled speech.
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Listening again…",
        transcript: "",
        pulseIntensity: 0,
        error: null,
        approvalConsentPending: false
      });
      this.record("listening_rearmed", "ok", "live_turn_complete_without_audio");
      return;
    }
    if (event.type === "interrupted") {
      this.releasePresentationPrivacy(event.turnId);
      this.clearRealtimeOutputResumeTimer();
      if (this.activeAudibleTurnId === event.turnId) this.activeAudibleTurnId = null;
      if (this.pausedOutputTurnId === event.turnId) this.pausedOutputTurnId = null;
      if (this.activeBrainTurnId === event.turnId) {
        this.activeBrainTurnId = null;
      }
      this.record("user_interrupted", "ok", event.reason);
      this.realtimeBridge.present({ type: "interrupted", turnId: event.turnId });
      if (this.latestNativeAnswer?.turnId === event.turnId) this.latestNativeAnswer = null;
      this.realtimeSession?.setInputMuted(this.currentState.muted);
      this.publish({
        mode: "voice",
        phase: "listening",
        status: "Interrupted · listening now…",
        pulseIntensity: 0,
        error: null,
        approvalConsentPending: false
      });
      return;
    }
    if (event.type === "error") {
      if (!event.recoverable && this.realtimeExpiryRecoveryGeneration === generation) {
        this.pauseRealtimeForTrustedRetry(new Error(event.message));
        return;
      }
      const fatalCode = safeOpsRealtimeVoiceFatalCode(event.fatalCode);
      if (!event.recoverable
        && fatalCode === "reconnect_exhausted"
        && this.activeBrainTurnId
        && this.environment.readTurnStatus) {
        const retainedTurnId = this.activeBrainTurnId;
        // WHY [BOL-VOICE-RECONNECT-RESULT-RETENTION-001]: exhausting the Live
        // presentation reconnect used to settle an admitted server turn as a
        // local FAILURE. A later authenticated same-ID final then lost to that
        // local row, so the saved answer never appeared. Retire only the failed
        // presentation transport and reuse exact-turn authenticated polling;
        // never resend work, open another Voice session, or synthesize audio.
        // Stop/new input/unmount/auth revocation fence this retained identity.
        // Falsifier: reconnect loss emits a local failure before server status,
        // starts a second request/session/speaker, or a fenced final reappears.
        this.record("voice_error", "blocked", fatalCode);
        this.retainedCanonicalTurnId = retainedTurnId;
        this.invalidate(true);
        this.realtimePresentationUnavailable = true;
        this.publish({
          mode: "text",
          micReadiness: "error",
          phase: "thinking",
          status: event.message || "Voice disconnected. Luna is still finishing in chat…",
          transcript: "",
          pulseIntensity: 0,
          muted: false,
          error: event.message || "Voice disconnected. Luna is still finishing in chat…",
          speechRetryAvailable: false,
          approvalConsentPending: false
        });
        this.pollRetainedCanonicalTurn(retainedTurnId);
        return;
      }
      if (event.recoverable) {
        // WHY [BOL-VOICE-LOCAL-REJECTOR-001]: the R340 ordinary follow-up
        // retained its final text but lost speech; its only local receipt was
        // generic English-message classification. Preserve finite owner/site
        // and reject-time counts, never message/PCM/exception content. This is
        // observation only: all recovery and playback decisions stay below.
        // Falsifier: capture saturation and audio scheduling collapse to the
        // same receipt, or a diagnostic changes the existing failure lifecycle.
        this.record("voice_error", "blocked", safeOpsRealtimeVoiceFatalCode(event.fatalCode), {
          ...event.localFailure,
          ...(event.failureOwner ? { failureOwner: event.failureOwner } : {})
        });
        if (event.failureOwner === "transport") {
          this.realtimePresentationUnavailable = true;
          this.activeAudibleTurnId = null;
          this.pausedOutputTurnId = null;
          this.clearRealtimeOutputResumeTimer();
          this.realtimeSession?.setInputMuted(true);
          if (event.turnId && this.activeBrainTurnId === event.turnId) {
            this.realtimeBridge.present({ type: "status-cue", turnId: event.turnId, text: event.message });
          }
          this.publish({
            mode: "text", micReadiness: "error", phase: "thinking",
            status: event.message, pulseIntensity: 0, muted: true,
            error: null, speechRetryAvailable: false, approvalConsentPending: false
          });
          return;
        }
        // WHY [BOL-VOICE-CUE-FAILURE-ISOLATION-001]: Android can reject a
        // delayed Web Audio cue even though the authenticated Live socket and
        // Luna's native answer are still healthy. Treating that optional hold
        // clip as assistant-audio failure closed the persistent Voice session,
        // switched to Text, and cold-started the next turn. Keep the exact turn
        // and one native renderer alive; the later answer still cancels/owns
        // output. Falsifier: a failed hold interrupts the turn, closes Voice,
        // starts another renderer, or prevents the native answer from playing.
        if (event.failureOwner === "status_cue") return;
        if (event.failureOwner === "acknowledgment_audio") {
          if (event.turnId && this.activeAudibleTurnId === event.turnId) {
            this.activeAudibleTurnId = null;
          }
          if (event.turnId && this.activeBrainTurnId === event.turnId) {
            // The exact failed presentation receipt prevents Voice authority;
            // keep the canonical text owner alive so the turn can fail or
            // finish honestly in Chat without a second mouth or lost request.
            this.publish({
              mode: "voice",
              phase: "thinking",
              status: "Voice acknowledgment could not play. Continuing in chat…",
              pulseIntensity: 0,
              error: null,
              speechRetryAvailable: false
            });
          }
          return;
        }
        const exactReply = this.latestNativeAnswer;
        if (event.turnId && (this.activeBrainTurnId === event.turnId
          || this.activeAudibleTurnId === event.turnId
          || this.pausedOutputTurnId === event.turnId
          || (exactReply?.turnId === event.turnId && exactReply.generation === generation))) {
          // WHY [BOL-VOICE-NATIVE-ONLY-001]: a rendered transcript is not
          // permission to re-synthesize it. Drop only the exact failed native
          // output owner, preserve its canonical Chat row, and keep the healthy
          // Live capture/session listening. Falsifier: failure starts a second
          // renderer, closes Voice, leaves loading behind, or settles a newer
          // turn.
          this.clearRealtimeOutputResumeTimer();
          this.recoverableAudioFailedTurns.add(event.turnId);
          this.releasePresentationPrivacy(event.turnId);
          while (this.recoverableAudioFailedTurns.size > 64) {
            const oldest = this.recoverableAudioFailedTurns.values().next().value as string | undefined;
            if (!oldest) break;
            this.recoverableAudioFailedTurns.delete(oldest);
          }
          this.realtimeBridge.present({
            type: "failed",
            turnId: event.turnId,
            message: "Voice audio stopped. Luna's reply can still finish in chat."
          });
          if (this.activeBrainTurnId === event.turnId) {
            this.activeBrainTurnId = null;
          }
          if (this.activeAudibleTurnId === event.turnId) this.activeAudibleTurnId = null;
          if (this.pausedOutputTurnId === event.turnId) this.pausedOutputTurnId = null;
          if (this.latestNativeAnswer?.turnId === event.turnId) this.latestNativeAnswer = null;
          this.realtimeSession?.setInputMuted(this.currentState.muted);
          this.publish({
            mode: "voice",
            phase: "listening",
            status: "Voice audio stopped. Listening again…",
            transcript: "",
            pulseIntensity: 0,
            error: null,
            speechRetryAvailable: false,
            approvalConsentPending: false
          });
          this.record("listening_rearmed", "ok", "recoverable_native_audio_failure");
        } else {
          this.publish({ status: event.message, error: null });
        }
      } else {
        this.failToText(
          event.message || "Realtime Voice stopped. Text still works normally.",
          fatalCode
        );
      }
      return;
    }
    if (event.type === "closed") {
      if (this.realtimeExpiryRecoveryGeneration === generation) {
        this.pauseRealtimeForTrustedRetry(new Error("fresh realtime session closed"));
        return;
      }
      this.failToText("Realtime Voice stopped. Text still works normally.", "realtime_closed");
    }
  }

  private rememberFinalizedRealtimeTurn(turnId: string): void {
    this.finalizedRealtimeTurns.add(turnId);
    while (this.finalizedRealtimeTurns.size > 64) {
      const oldest = this.finalizedRealtimeTurns.values().next().value as string | undefined;
      if (!oldest) break;
      this.finalizedRealtimeTurns.delete(oldest);
      this.realtimeActivityStartedAt.delete(oldest);
      this.realtimeActivityEndedAt.delete(oldest);
      this.realtimeInterimSeen.delete(oldest);
      this.realtimeFirstPartialAt.delete(oldest);
    }
  }


  private markAnswerVisible(generation: number, turnId = this.currentTurnId): void {
    if (!this.isCurrent(generation)
      || (turnId && this.activeBrainTurnId !== turnId)
      || this.currentState.answerLatencyMs !== null) return;
    const answerLatencyMs = this.elapsedFromFinal();
    this.publish({ answerLatencyMs });
    this.record("answer_visible");
  }

  private recordInterruptionIfActive(detail: string): void {
    if (!["thinking", "speaking"].includes(this.currentState.phase)) return;
    this.record("user_interrupted", "ok", detail);
  }

  private pauseOutputForPotentialInput(inputTurnId: string): void {
    const targetTurnId = this.activeAudibleTurnId ?? this.activeBrainTurnId;
    if (!targetTurnId || targetTurnId === inputTurnId || this.pausedOutputTurnId) return;
    if (this.realtimeSession?.pauseOutput(targetTurnId)) this.pausedOutputTurnId = targetTurnId;
  }

  private interruptPausedOutput(inputTurnId: string): void {
    const targetTurnId = this.pausedOutputTurnId
      ?? this.realtimeContinuationTargetByTurn.get(inputTurnId)
      ?? this.activeAudibleTurnId
      ?? this.activeBrainTurnId;
    if (!targetTurnId || targetTurnId === inputTurnId) return;
    this.clearRealtimeOutputResumeTimer();
    this.pausedOutputTurnId = null;
    this.realtimeContinuationTargetByTurn.delete(inputTurnId);
    // WHY [BOL-VOICE-SEMANTIC-BARGE-001]: raw activity cannot alter playback.
    // Recognized words may pause the presentation clock; a complete substantive
    // replacement or terminal Stop destroys queued PCM and cancels its exact
    // server owner. Falsifier: noise/backchannel reaches clearOutput, or Stop
    // lets the old suffix resume.
    this.realtimeSession?.clearOutput(targetTurnId, "barge_in");
    this.releasePresentationPrivacy(targetTurnId);
  }

  private lockPresentationPrivacy(turnId: string): void {
    // WHY [BOL-VOICE-PRESENTATION-PRIVACY-LEASE-001]: Live snapshots privacy
    // when a turn starts, while paint/copy otherwise read mutable app state.
    // Hold one turn-keyed presentation lease through audible, no-audio, error,
    // interruption, or recovered settlement; a stale end may release only its
    // own key. The canonical raw TurnResult is never rewritten here.
    // Falsifier: either Private control changes mid-turn, heard and painted
    // projections split, a stale end unlocks a replacement, or raw result bytes
    // change as part of presentation masking.
    const wasUnlocked = this.presentationPrivacyLockedTurns.size === 0;
    this.presentationPrivacyLockedTurns.add(turnId);
    if (wasUnlocked) this.realtimeBridge.presentationPrivacyLockChange?.(true);
  }

  private releasePresentationPrivacy(turnId: string): void {
    if (!this.presentationPrivacyLockedTurns.delete(turnId)) return;
    if (this.presentationPrivacyLockedTurns.size === 0) {
      this.realtimeBridge.presentationPrivacyLockChange?.(false);
    }
  }

  private clearPresentationPrivacyLocks(): void {
    if (this.presentationPrivacyLockedTurns.size === 0) return;
    this.presentationPrivacyLockedTurns.clear();
    this.realtimeBridge.presentationPrivacyLockChange?.(false);
  }

  private resumePausedOutput(inputTurnId: string, rememberContinuation = false): void {
    const targetTurnId = this.pausedOutputTurnId
      ?? this.realtimeContinuationTargetByTurn.get(inputTurnId)
      ?? null;
    if (!targetTurnId) return;
    this.clearRealtimeOutputResumeTimer();
    if (rememberContinuation) this.realtimeContinuationTargetByTurn.set(inputTurnId, targetTurnId);
    if (this.realtimeSession?.resumeOutput(targetTurnId)) {
      this.pausedOutputTurnId = null;
    }
  }

  private scheduleRealtimeOutputResume(inputTurnId: string, delayMs: number): void {
    this.clearRealtimeOutputResumeTimer();
    this.realtimeOutputResumeTimer = this.environment.setTimer(() => {
      this.realtimeOutputResumeTimer = null;
      this.resumePausedOutput(inputTurnId);
    }, delayMs);
  }

  private clearRealtimeOutputResumeTimer(): void {
    if (this.realtimeOutputResumeTimer === null) return;
    this.environment.clearTimer(this.realtimeOutputResumeTimer);
    this.realtimeOutputResumeTimer = null;
  }

  private record(
    name: Parameters<typeof recordOpsVoiceMetric>[0]["name"],
    status: "ok" | "blocked" | "failed" = "ok",
    safeDetail?: string,
    localFailure?: Parameters<typeof recordOpsVoiceMetric>[0]["localFailure"]
  ): void {
    if (!this.currentTurnId) return;
    recordOpsVoiceMetric({
      turnId: this.currentTurnId,
      name,
      atMs: this.now(),
      ...(this.finalTranscriptAt > 0 ? { elapsedFromFinalMs: this.elapsedFromFinal() } : {}),
      status,
      ...(safeDetail ? { safeDetail } : {}),
      ...(localFailure ? { localFailure } : {})
    });
  }

  private elapsedFromFinal(): number {
    return this.finalTranscriptAt > 0 ? Math.max(0, Math.round(this.now() - this.finalTranscriptAt)) : 0;
  }

  private now(): number {
    return this.environment.now?.() ?? performance.now();
  }

  private failToText(message: string, fatalCode: OpsVoiceFatalCode): void {
    // WHY [BOL-VOICE-FATAL-ORIGIN-001]: the Sep11 pre-prompt Voice failure
    // survived only as voice_turn_failed: case-sensitive prose classification
    // collapsed unrelated fatal owners, then the ordinary Text submit cleared
    // the footer and cleanup retired the transport. Preserve the code-owned
    // caller/gateway discriminator in the existing bounded metric BEFORE those
    // actions. The next ordinary replay must identify its actual owner; neither
    // a 15-second-looking gap nor this diagnostic establishes a provider cause.
    // Falsifier: distinct origins collapse, raw errors/content enter metrics,
    // Text/retry erases the receipt, or failure/Stop/result behavior changes.
    this.record("voice_error", "failed", fatalCode);
    const failedTurnId = this.activeBrainTurnId;
    if (failedTurnId) {
      // WHY [BOL-VOICE-FATAL-TURN-SETTLEMENT-001]: closing a fatal realtime
      // generation used to turn the pending row into an empty cancellation,
      // leaving only a red footer and making Chat look frozen. Settle that exact
      // row first: the bridge preserves a complete answer, while an unfinished
      // turn becomes a visible recoverable failure with the existing retry.
      // Normal Stop/barge interruptions remain silent transport control.
      // Falsifier: a fatal frame leaves loading/blank Chat, overwrites a complete
      // answer, adds error prose for Stop, or settles a different/newer turn.
      this.realtimeBridge.present({ type: "failed", turnId: failedTurnId, message });
    }
    this.invalidate(true);
    this.publish({
      mode: "text",
      micReadiness: "error",
      phase: "error",
      status: message,
      transcript: "",
      pulseIntensity: 0,
      muted: false,
      error: message,
      speechRetryAvailable: false
    });
  }

  private invalidate(releaseOwner: boolean): number {
    this.activationRequest += 1;
    this.generation += 1;
    this.latestNativeAnswer = null;
    this.realtimePresentationUnavailable = false;
    const realtimeSession = this.realtimeSession;
    this.realtimeSession = null;
    this.realtimeOpenPromise = null;
    this.realtimeExpiryRecoveryGeneration = null;
    this.activeBrainTurnId = null;
    this.activeAudibleTurnId = null;
    this.pausedOutputTurnId = null;
    this.clearRealtimeOutputResumeTimer();
    this.finalizedRealtimeTurns.clear();
    this.realtimeActivityStartedAt.clear();
    this.realtimeActivityEndedAt.clear();
    this.realtimeInterimSeen.clear();
    this.realtimeExplicitHoldInterimSeen.clear();
    this.realtimeFirstPartialAt.clear();
    this.realtimeContinuationTargetByTurn.clear();
    this.recoverableAudioFailedTurns.clear();
    this.clearPresentationPrivacyLocks();
    this.currentState = { ...this.currentState, speechRetryAvailable: false, approvalConsentPending: false };
    realtimeSession?.close();
    if (releaseOwner) this.mediaOwner.release("voice");
    return this.generation;
  }

  private isCurrent(generation: number): boolean {
    // Input mode may have moved to Text after loss of the voice provider, but
    // this generation still owns its one unfinished canonical result. Explicit
    // Stop/new input invalidates that generation normally.
    return this.mounted && this.generation === generation
      && (this.currentState.mode === "voice" || this.realtimePresentationUnavailable);
  }

  private admitHistory(): boolean {
    // WHY [BOL-VOICE-HISTORY-ADMISSION-001]: an empty hydration placeholder
    // previously entered both initial and replacement Live sessions. Keep the
    // generation/close owner intact, and admit only a current ready bridge.
    if (this.realtimeBridge.historyReady?.() !== false) return true;
    this.selectText();
    this.publish({ status: "Loading conversation…" });
    return false;
  }

  private publish(patch: Partial<OpsVoiceState>): void {
    const finishTiming = beginOpsVoiceSyncTiming("publish");
    try {
    this.currentState = { ...this.currentState, ...patch };
    for (const listener of this.listeners) listener();
    } finally { finishTiming?.(); }
  }
}

export function useOpsVoice(input: {
  mediaOwner: OpsMediaOwnerController;
  historyReady?: boolean;
  publicReleaseId?: OpsSha256;
  realtimeHistory?: readonly OpsAssistantHistoryMessage[];
  workspaceContext?: unknown;
  presentationPrivacy?: OpsPresentationPrivacy;
  onPresentationPrivacyLockChange?: (locked: boolean) => void;
  onRealtimeTurn?: (event: OpsRealtimeTurnPresentation) => void | boolean | Promise<boolean>;
}): OpsVoiceController & { state: OpsVoiceState } {
  const historyReadyRef = useRef(input.historyReady !== false);
  historyReadyRef.current = input.historyReady !== false;
  const historyRef = useRef(input.realtimeHistory ?? []);
  historyRef.current = input.realtimeHistory ?? [];
  const publicReleaseIdRef = useRef(input.publicReleaseId ?? null);
  publicReleaseIdRef.current = input.publicReleaseId ?? null;
  const workspaceContextRef = useRef(input.workspaceContext ?? null);
  workspaceContextRef.current = input.workspaceContext ?? null;
  const presentationPrivacyRef = useRef<OpsPresentationPrivacy>(
    input.presentationPrivacy ?? "MASK_CONTACT_DETAILS"
  );
  presentationPrivacyRef.current = input.presentationPrivacy ?? "MASK_CONTACT_DETAILS";
  const presentationRef = useRef(input.onRealtimeTurn);
  presentationRef.current = input.onRealtimeTurn;
  const presentationPrivacyLockRef = useRef(input.onPresentationPrivacyLockChange);
  presentationPrivacyLockRef.current = input.onPresentationPrivacyLockChange;
  const freshnessRef = useRef<OpsClientFreshnessGate | null | undefined>(undefined);
  if (freshnessRef.current === undefined) {
    freshnessRef.current = createBrowserOpsClientFreshnessGate();
  }
  const controllerRef = useRef<OpsVoiceController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new OpsVoiceController(
      input.mediaOwner,
      undefined,
      {
        history: () => historyRef.current,
        historyReady: () => historyReadyRef.current,
        publicReleaseId: () => publicReleaseIdRef.current,
        workspaceContext: () => workspaceContextRef.current,
        presentationPrivacy: () => presentationPrivacyRef.current,
        presentationPrivacyLockChange: (locked) => presentationPrivacyLockRef.current?.(locked),
        present: (event) => presentationRef.current?.(event)
      },
      freshnessRef.current
    );
  }
  const state = useSyncExternalStore(
    controllerRef.current.subscribe,
    controllerRef.current.getSnapshot,
    controllerRef.current.getSnapshot
  );
  useEffect(() => {
    if (input.historyReady === false) controllerRef.current?.selectText();
  }, [input.historyReady]);
  useEffect(() => {
    const controller = controllerRef.current;
    const freshness = freshnessRef.current;
    controller?.mount();
    if (!controller || !freshness || typeof window === "undefined" || typeof document === "undefined") {
      return () => controller?.unmount();
    }
    const checkWhenVisibleAndIdle = (): void => {
      if (document.visibilityState !== "visible" || controller.getSnapshot().mode !== "text") return;
      void freshness.check();
    };
    checkWhenVisibleAndIdle();
    document.addEventListener("visibilitychange", checkWhenVisibleAndIdle);
    window.addEventListener("pageshow", checkWhenVisibleAndIdle);
    window.addEventListener("focus", checkWhenVisibleAndIdle);
    return () => {
      document.removeEventListener("visibilitychange", checkWhenVisibleAndIdle);
      window.removeEventListener("pageshow", checkWhenVisibleAndIdle);
      window.removeEventListener("focus", checkWhenVisibleAndIdle);
      controller.unmount();
    };
  }, []);
  return Object.assign(controllerRef.current, { state });
}

function safeVoiceErrorDetail(message: string): string {
  if (message.includes("denied")) return "microphone_denied";
  if (message.includes("not supported")) return "unsupported";
  if (message.includes("silent")) return "speech_silent";
  if (message.includes("voice")) return "speech_unavailable";
  return "voice_turn_failed";
}

function supportsVoice(environment: OpsVoiceEnvironment): boolean {
  return Boolean(environment.openRealtimeSession);
}

function realtimeUnsupportedMessage(reason?: string | null): string {
  if (reason === "insecure_context") return "Realtime Voice needs a secure connection. Text still works normally.";
  if (reason === "get_user_media_missing") return "This browser cannot open the microphone for realtime Voice. Text still works normally.";
  if (reason === "audio_worklet_missing" || reason === "audio_context_missing") {
    return "This browser cannot play realtime Voice audio. Text still works normally.";
  }
  return "Realtime Voice is not supported in this browser. Text still works normally.";
}

function realtimeStartErrorMessage(error: unknown): string {
  const value = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");
  if (/notallowed|not-allowed|permission|denied/iu.test(value)) {
    return "Microphone access was denied. Text still works normally.";
  }
  if (/notfound|not-found|audio.*capture|device/iu.test(value)) {
    return "No microphone is available. Text still works normally.";
  }
  return "Realtime Voice is unavailable right now. Text still works normally.";
}

export function createBrowserVoiceEnvironment(): OpsVoiceEnvironment {
  if (typeof window === "undefined") {
    return {
      setTimer: (callback, milliseconds) => globalThis.setTimeout(callback, milliseconds),
      clearTimer: (timer) => globalThis.clearTimeout(timer as ReturnType<typeof setTimeout>),
      realtimeUnsupportedReason: "server",
      openRealtimeSession: null
    };
  }
  const browser = window as unknown as { speechSynthesis?: { cancel(): void } };
  // WHY [BOL-VOICE-PRODUCTION-RENDERER-BOUNDARY-001]: the accepted Samsung
  // failure began with Gemini Live/Aoede, cut off, then continued in a visibly
  // different browser voice. The production environment therefore cancels any
  // document-leftover Web Speech utterance once, then exposes only the native
  // realtime PCM session to the controller. Falsifier: the environment returns
  // any browser speech capability or a production reply can invoke TTS.
  try { browser.speechSynthesis?.cancel(); } catch { /* stale browser queue was already unavailable */ }
  const realtimeUnsupportedReason = getOpsRealtimeVoiceUnsupportedReason();
  return {
    setTimer: (callback, milliseconds) => window.setTimeout(callback, milliseconds),
    clearTimer: (timer) => window.clearTimeout(timer as number),
    readTurnStatus: api.opsTurnStatus,
    cancelTurn: api.cancelOpsTextTurn,
    realtimeUnsupportedReason,
    openRealtimeSession: realtimeUnsupportedReason ? null : openOpsRealtimeVoiceSession
  };
}
